import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  styled,
  Typography,
} from "@mui/material";

import RichTextField from "../../../ui/components/Form/RichTextField";

import * as yup from "yup";
import {
  AspectRatio,
  InsertPhoto,
  TextFields,
  YouTube,
} from "@mui/icons-material";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import ControlledVideoInput from "../../../ui/components/DragAndDropFileInput/ControlledVideoInput";

import CustomTextField from "../../../ui/components/Form/TextField";
import { useUploadVideoToYoutube } from "../api/uploadVideoToYoutube";
import { useCreateSocialMediaPost } from "../api/createSocialMediaPost";
import { useInsertPostAttachment } from "../api/insertPostAttachment";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import ReactPlayer from "react-player";
import { baseUrl } from "../../../shared/config";
import BackgroundPicker from "./CreateSocialMediaPost/BackgroundPicker";
import ImageField from "../../../ui/components/Form/ImageField";
import _cookies from "../../../shared/utils/_cookies";
import { useDisclosure } from "../../../shared/hooks/useDisclosure";
import { useTenantParameters } from "../../misc/api/getTenantParameters";

const UploaderButtons = styled(IconButton)(({ theme }) => ({
  fontSize: "50px",
}));

const FormContentWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "aspectRatio",
})(({ theme, aspectRatio }) => ({
  overflow: "auto",

  ".InputBox": {
    height: "100%",
    maxHeight: "100%",
  },
  ".aspectRatioBox": {
    aspectRatio,
    width: "100%",
    height: "max-content",
  },
}));

const schema = yup.object().shape({
  type: yup.string(),
  richText: yup.string(),
  textContent: yup.string(),
  backgroundImage: yup.string(),
  files: yup.array().when("type", (type, _schema) => {
    if (type != "TEXTO" && type != "VIDEO LINK") {
      return _schema.min(1, "Escolha pelo menos um arquivo");
    }
    return _schema;
  }),
  videoURL: yup.string().when("type", (type, _schema) => {
    if (type == "VIDEO LINK") {
      return _schema.required("Campo obrigatório");
    }
    return _schema;
  }),
});

const defaultValues = {
  titulo: "",
  textContent: "",
  richText:
    '<h2 class="ql-align-center"><strong>O que você está pensando?</strong></h2>',
  aspectRatio: "4 / 5",
  title: "",
  videoURL: "",
  backgroundImage: `${baseUrl}/Membros/images/BACKGROUND1.JPG`,
  type: "TEXTO",
  files: [],
};

const AspectRatioInput = ({ formMethods }) => {
  const [optionsOpen, setOptionsOpen] = useState(false);

  const handleOpen = () => {
    setOptionsOpen(true);
  };
  const handleClose = () => {
    setOptionsOpen(false);
  };
  return (
    <>
      <IconButton onClick={handleOpen}>
        <AspectRatio />
      </IconButton>
      <Controller
        control={formMethods.control}
        name={"aspectRatio"}
        render={({ field }) => {
          return (
            <Select
              open={optionsOpen}
              onClose={handleClose}
              sx={{ width: "0px", opacity: "0" }}
              label="Proporção"
              {...field}
            >
              <MenuItem value={"1 / 1"}> 1 : 1</MenuItem>
              <MenuItem value={"4 / 5"}> 4 : 5</MenuItem>
              <MenuItem value={"16 / 9"}> 16 : 9</MenuItem>
            </Select>
          );
        }}
      ></Controller>
    </>
  );
};

const FormFooter = ({ formMethods, hideBackgroundPicker = true }) => {
  return (
    <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
      <AspectRatioInput formMethods={formMethods} />
      <CustomTextField
        fieldName={"textContent"}
        formMethods={formMethods}
        label="Adicione uma legenda"
        multiline={true}
        rows={4}
        sx={{ width: "100%" }}
      />
    </Box>
  );
};

const RenderFormContent = ({ type, formMethods }) => {
  const backgroundImage = formMethods.watch("backgroundImage");
  const aspectRatioValue = formMethods.watch("aspectRatio");
  switch (type) {
    case "TEXTO":
      return (
        <Stack
          sx={{
            height: "100%",
          }}
          spacing={1}
        >
          <Box
            sx={{
              ".quill": {
                ".ql-editor": { overflow: "hidden" },
                ".ql-container": {
                  aspectRatio: aspectRatioValue,

                  backgroundImage: `url(${backgroundImage})`,
                  backgroundSize: `cover`,
                },
              },
            }}
          >
            <RichTextField
              fieldName={"richText"}
              formMethods={formMethods}
              RichTextInputProps={{
                modules: {
                  toolbar: [
                    [{ header: [1, 2, 3, 4, 5, 6, false] }],
                    [{ color: [] }],
                    ["bold", "italic", "underline"],

                    [{ align: [] }],
                  ],
                  "emoji-toolbar": true,
                  "emoji-textarea": true,
                  "emoji-shortname": true,
                },
                formats: [
                  "header",
                  "color",
                  "bold",
                  "italic",
                  "underline",
                  "align",
                  "emoji",
                ],
              }}
            />
          </Box>
          <BackgroundPicker formMethods={formMethods} />
          <FormFooter formMethods={formMethods} hideBackgroundPicker={false} />
        </Stack>
      );
    case "IMAGEM":
      return (
        <Stack sx={{ height: "100%", paddingTop: 1 }} spacing={1}>
          <Box className="aspectRatioBox">
            <ImageField
              formMethods={formMethods}
              fieldName={"files"}
              multiple={true}
            />
          </Box>
          <FormFooter formMethods={formMethods} />
        </Stack>
      );
    case "VIDEO":
      return (
        <Stack sx={{ height: "100%", paddingTop: 1 }} spacing={1}>
          <Box className="aspectRatioBox">
            <ControlledVideoInput
              accept="video/mp4,video/x-m4v,video/*"
              name="files"
              control={formMethods.control}
              register={formMethods.register}
              // helperText={"mp4"}
              error={false}
              multiple={false}
              disableTitleInput={true}
            />
          </Box>
          <FormFooter formMethods={formMethods} />
        </Stack>
      );
    case "DOCUMENTO":
      return <>documento</>;
    case "VIDEO LINK":
      const videoURL = formMethods.watch("videoURL").replace("shorts", "embed");

      return (
        <Stack
          sx={{ height: "100%", width: "100%", paddingTop: 1 }}
          spacing={1}
        >
          <CustomTextField
            fieldName={"videoURL"}
            formMethods={formMethods}
            label="URL de vídeo YouTube"
          />
          {!!videoURL && (
            <Box className="aspectRatioBox" sx={{ backgroundColor: "#AFAFAF" }}>
              <ReactPlayer width="100%" height="100%" url={videoURL} />
            </Box>
          )}
          <FormFooter formMethods={formMethods} />
        </Stack>
      );

    default:
      return <></>;
  }
};

export default function CreateSocialMediaPost({ themeId = 0 }) {
  const tenantParamsQ = useTenantParameters();
  const moderationWarning = useDisclosure(false);
  const [expanded, setExpanded] = useState(false);
  const createPostMutation = useCreateSocialMediaPost();
  const uploadVideoMutation = useUploadVideoToYoutube();
  const insertAttachmentMutation = useInsertPostAttachment();
  const queryClient = useQueryClient();

  const handleExpand = () => {
    setExpanded(true);
  };

  const handleMinimize = () => {
    setExpanded(false);
  };

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ["socialMediaFeed"],
    });
  };
  const handleSuccess = () => {
    invalidateQueries();
    handleMinimize();
    if (tenantParamsQ.data.EXIGIR_APROVACAO_REDE_SOCIAL === "S") {
      moderationWarning.open();
    }
  };

  const handleError = () => {
    handleMinimize();
  };

  const fillValues = {
    studentId: _cookies.getItem("auth").user?.alunoid,
    studentName: _cookies.getItem("auth").user?.nome,
    themeId:
      tenantParamsQ.data.EXIGIR_APROVACAO_REDE_SOCIAL === "S"
        ? Number(themeId) === 0
          ? 10000
          : `-${themeId}`
        : themeId,
    title: "",
    contentType: "",
    text: "",
    content: "",
    aspectRatio: "",
    visible: "S",
    extension: "",
    chapterId: "",
  };
  const handleSubmitVideoPost = async (values) => {
    await createPostMutation.mutate(
      {
        ...fillValues,
        contentType: "VIDEO",
        text: values.textContent,
        aspectRatio: values.aspectRatio,
        files: [values.files[0].file],
        // extension: "MP4",
      },
      { onSuccess: handleSuccess, onError: handleError }
    );
  };

  const handleSubmitVideoLinkPost = async (values) => {
    await createPostMutation.mutate(
      {
        ...fillValues,
        contentType: "LINK",
        text: values.textContent,
        content: values.videoURL,
        aspectRatio: values.aspectRatio,
      },
      { onSuccess: handleSuccess, onError: handleError }
    );
  };

  const handleSubmitImagePost = async (values) => {
    await createPostMutation.mutate(
      {
        ...fillValues,
        contentType: "IMAGEM",
        text: values.textContent,
        aspectRatio: values.aspectRatio,
        files: values.files,
      },
      {
        onSuccess: handleSuccess,
        onError: handleError,
      }
    );
  };
  const handleSubmitTextPost = async (values) => {
    await createPostMutation.mutate(
      {
        ...fillValues,
        contentType: "TEXTO",
        title: `${values.richText}`,
        text: values.textContent,
        vimeoid: values.backgroundImage,
        aspectRatio: values.aspectRatio,
      },
      { onSuccess: handleSuccess, onError: handleError }
    );
  };

  const handleSubmit = async (values) => {
    switch (values.type) {
      case "VIDEO LINK":
        await handleSubmitVideoLinkPost(values);
        break;
      case "VIDEO":
        await handleSubmitVideoPost(values);
        break;
      case "IMAGEM":
        await handleSubmitImagePost(values);
        break;
      case "TEXTO":
        await handleSubmitTextPost(values);
        break;
      default:
        break;
    }

    formMethods.reset();
  };

  const formMethods = useForm({ resolver: yupResolver(schema), defaultValues });
  const aspectRatioValue = formMethods.watch("aspectRatio");

  const postType = formMethods.watch("type");

  const isCurrentType = (type) => {
    return type === postType;
  };

  const changePostType = (newType) => {
    handleExpand();
    formMethods.reset();
    formMethods.setValue("type", newType);
    if (newType === "VIDEO" || newType === "VIDEO LINK") {
      formMethods.setValue("aspectRatio", "16 / 9");
    }
  };
  const isAnyMutationLoading =
    createPostMutation.isLoading ||
    insertAttachmentMutation.isLoading ||
    uploadVideoMutation.isLoading;
  const handleCancelPost = () => {
    handleMinimize();
    formMethods.reset();
  };

  return (
    <Accordion
      expanded={expanded}
      sx={{
        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        sx={{
          "&:before": {
            display: "none",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <UploaderButtons
            disabled={
              (isCurrentType("TEXTO") && expanded) || isAnyMutationLoading
            }
            onClick={() => changePostType("TEXTO")}
            fontSize="large"
          >
            <TextFields />
          </UploaderButtons>
          {/* <UploaderButtons
            disabled={
              (isCurrentType("VIDEO") && expanded) || isAnyMutationLoading
            }
            onClick={() => changePostType("VIDEO")}
            fontSize="large"
          >
            <VideoCameraBack />
          </UploaderButtons> */}
          <UploaderButtons
            disabled={
              (isCurrentType("VIDEO LINK") && expanded) || isAnyMutationLoading
            }
            onClick={() => changePostType("VIDEO LINK")}
            fontSize="large"
          >
            <YouTube />
          </UploaderButtons>
          <UploaderButtons
            disabled={
              (isCurrentType("IMAGEM") && expanded) || isAnyMutationLoading
            }
            onClick={() => changePostType("IMAGEM")}
          >
            <InsertPhoto />
          </UploaderButtons>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <Dialog open={moderationWarning.isOpen} fullWidth maxWidth="sm">
          <DialogTitle>Aviso</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Seu post foi criado e foi enviado para análise de um gestor
              responsável.{" "}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={moderationWarning.close} variant="contained">
              OK
            </Button>
          </DialogActions>
        </Dialog>
        <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
          <Stack>
            <FormContentWrapper
              className="wrapper"
              aspectRatio={aspectRatioValue}
            >
              {isAnyMutationLoading ? (
                <Box className="aspectRatioBox flex flex-col items-center justify-center">
                  <CircularProgress />
                  <Typography>
                    Carregando conteúdo, aguarde um momento
                  </Typography>
                </Box>
              ) : (
                <RenderFormContent type={postType} formMethods={formMethods} />
              )}
            </FormContentWrapper>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button
                onClick={handleCancelPost}
                disabled={isAnyMutationLoading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isAnyMutationLoading}>
                Salvar
              </Button>
            </Box>
          </Stack>
        </form>
      </AccordionDetails>
    </Accordion>
  );
}
